import { useEffect, useMemo } from 'react'
import { useSearchParams } from 'react-router-dom'
import useSWR from 'swr'
import { format } from 'date-fns'
import { CalendarIcon, Cross2Icon, PlusIcon } from '@radix-ui/react-icons'

import AppSelect from '@/components/zthd/app-select'
import { DropdownCountry1 } from '@/components/DropdownCountry'
import Loader from '@/components/zthd/Loader'
import { Button } from '@/components/ui/button'
import { Calendar } from '@/components/ui/calendar'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import { formatUSD, formatPercent } from '@/lib/format-utils'
import { cn } from '@/lib/utils'

const MAX_DATES = 12
const DAY_MS = 24 * 60 * 60 * 1000

const toYmd = (date) => format(date, 'yyyy-MM-dd')
const parseYmd = (ymd) => {
  const [y, m, d] = ymd.split('-').map(Number)
  return new Date(y, m - 1, d)
}
const shiftDays = (ymd, days) =>
  toYmd(new Date(parseYmd(ymd).getTime() + days * DAY_MS))

// 默认对比「前天」与其一周前（同星期几），前天的数据相对完整
const defaultDates = () => {
  const latest = toYmd(new Date(Date.now() - 2 * DAY_MS))
  return [latest, shiftDays(latest, -7)]
}

const AGE_BUCKETS = [
  { key: 'd0', label: 'D0' },
  { key: 'd1_3', label: 'D1–3' },
  { key: 'd4_7', label: 'D4–7' },
  { key: 'd8_14', label: 'D8–14' },
  { key: 'd15_30', label: 'D15–30' },
  { key: 'd31_60', label: 'D31–60' },
  { key: 'd61p', label: 'D61+' },
]

// 分段 ROAS：各段收入 ÷ 该段用户安装窗口内的花费（D0 当日，D1–3 前 1–3 天，……，D31–60 前 31–60 天）
const EFFICIENCY_ROWS = [
  { key: 'd0', label: 'D0 ROAS（÷ 当日花费）' },
  { key: 'd1_3', label: 'D1–3 ROAS（÷ 前 1–3 天花费）' },
  { key: 'd4_7', label: 'D4–7 ROAS（÷ 前 4–7 天花费）' },
  { key: 'd8_14', label: 'D8–14 ROAS（÷ 前 8–14 天花费）' },
  { key: 'd15_30', label: 'D15–30 ROAS（÷ 前 15–30 天花费）' },
  { key: 'd31_60', label: 'D31–60 ROAS（÷ 前 31–60 天花费）' },
]

const RevenueAgeStructure = () => {
  const [searchParams, setSearchParams] = useSearchParams()
  const pid = searchParams.get('pid') || '11'
  const country = searchParams.get('country') || 'total'
  const dates = useMemo(() => {
    const raw = searchParams.get('dates')
    const list = raw ? raw.split(',').filter(Boolean) : []
    // 列始终按日期降序（最新在左）、去重，与添加顺序无关
    return list.length
      ? [...new Set(list)].sort((a, b) => b.localeCompare(a))
      : defaultDates()
  }, [searchParams])

  const update = (patch) => {
    const next = new URLSearchParams(searchParams)
    Object.entries(patch).forEach(([k, v]) => {
      if (v === null || v === undefined || v === '') next.delete(k)
      else next.set(k, v)
    })
    setSearchParams(next, { replace: true })
  }

  // 首次进入时把默认值写进 URL，便于分享链接
  useEffect(() => {
    if (!searchParams.get('dates')) {
      update({ pid, country, dates: dates.join(',') })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const setDates = (list) => update({ dates: list.join(',') })
  const changeDate = (index, ymd) => {
    const next = [...dates]
    next[index] = ymd
    setDates(next)
  }
  const removeDate = (index) => {
    if (dates.length <= 1) return
    setDates(dates.filter((_, i) => i !== index))
  }
  const addDate = () => {
    if (dates.length >= MAX_DATES) return
    // 新列：最晚一列往后一周；超过前天则改为最早一列往前一周（显示时会重新排序）
    const latestAllowed = toYmd(new Date(Date.now() - 2 * DAY_MS))
    const next = shiftDays(dates[0], 7)
    setDates([
      ...dates,
      next <= latestAllowed ? next : shiftDays(dates[dates.length - 1], -7),
    ])
  }

  const { data, isLoading, error } = useSWR([
    '/troas/revenue-age-structure',
    {
      pid,
      country: country === 'total' ? undefined : country,
      dates: dates.join(','),
    },
  ])

  const days = useMemo(() => {
    const byDate = new Map((data?.days ?? []).map((d) => [d.date, d]))
    return dates.map((date) => byDate.get(date) ?? null)
  }, [data, dates])

  return (
    <div className="flex flex-col gap-4 py-4 sm:gap-2 sm:py-2">
      <div className="flex items-center gap-4 flex-wrap sm:gap-2">
        <AppSelect value={pid} onValueChange={(v) => update({ pid: v })} />
        <DropdownCountry1
          value={country}
          update={(v) => update({ country: v === 'total' ? null : v })}
        />
        {isLoading && <Loader>Loading...</Loader>}
        {error && (
          <span className="text-sm text-destructive">{error.message}</span>
        )}
      </div>

      <div className="text-xs text-muted-foreground">
        口径：total_{'{国家}'}_{'{pid}'} 汇总键（含自然量），当日收入 = IAA +
        IAP，按用户安装到该日的队列年龄拆分； IAP 的 D4–7
        常为负（退款窗口）；最近两天的 IAP 仍在回填。
        {data?.campaignIds?.length === 0 && ' 当前产品/国家没有汇总键。'}
      </div>

      <div className="overflow-x-auto">
        <table className="min-w-[640px] border-collapse text-sm">
          <thead>
            <tr className="border-b bg-gray-50">
              <th className="p-2 text-left font-medium text-gray-500 w-40">
                指标
              </th>
              {dates.map((date, i) => (
                <th
                  key={`${date}-${i}`}
                  className="group relative p-2 text-right font-medium"
                >
                  <div className="flex justify-end">
                    <DateCell
                      value={date}
                      onChange={(ymd) => changeDate(i, ymd)}
                    />
                  </div>
                  <div className="text-xs font-normal text-gray-400">
                    {format(parseYmd(date), 'EEE')}
                  </div>
                  {/* 移除按钮不占位，悬停列头时才显示 */}
                  {dates.length > 1 && (
                    <button
                      type="button"
                      onClick={() => removeDate(i)}
                      title="移除该列"
                      className="absolute -right-1 -top-1 hidden h-5 w-5 items-center justify-center rounded-full border bg-white text-gray-500 shadow-sm hover:text-red-600 group-hover:flex"
                    >
                      <Cross2Icon className="h-3 w-3" />
                    </button>
                  )}
                </th>
              ))}
              <th className="p-2 text-left font-normal">
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7"
                  onClick={addDate}
                  disabled={dates.length >= MAX_DATES}
                  title="添加日期列"
                >
                  <PlusIcon className="h-4 w-4" />
                </Button>
              </th>
            </tr>
          </thead>
          <tbody className="divide-y">
            <SectionRow title="总览" span={dates.length + 2} />
            <MoneyRow
              label="当日总收入"
              days={days}
              get={(d) => d.total}
              bold
            />
            <MoneyRow label="当日花费" days={days} get={(d) => d.cost} />
            <ValueRow
              label="安装数"
              days={days}
              render={(d) => d.installs.toLocaleString('en-US')}
            />

            <SectionRow
              title="花费效率（收入 ÷ 产生它的那批用户的花费，不受当日花费基数影响）"
              span={dates.length + 2}
            />
            <ValueRow
              label="CPI"
              days={days}
              render={(d) =>
                d.installs > 0 ? formatUSD(d.cost / d.installs, 3, 3) : '-'
              }
            />
            {EFFICIENCY_ROWS.map((r) => (
              <ValueRow
                key={r.key}
                label={r.label}
                days={days}
                bold={r.key === 'd0'}
                render={(d) => {
                  const base = d.windowCost?.[r.key] ?? 0
                  return base > 0 ? formatPercent(d[r.key] / base, 1, 1) : '-'
                }}
              />
            ))}
            {/* D61+ 没有可对应的花费窗口，给它占当日总收入的比例 */}
            <ValueRow
              label="D61+ 占当日收入"
              days={days}
              render={(d) =>
                d.total > 0 ? formatPercent(d.d61p / d.total, 1, 1) : '-'
              }
            />

            <SectionRow
              title="按队列年龄（IAA + IAP）"
              span={dates.length + 2}
            />
            {AGE_BUCKETS.map((b) => (
              <MoneyRow
                key={b.key}
                label={b.label}
                days={days}
                get={(d) => d[b.key]}
                share={(d) => d.total}
                bold={b.key === 'd0'}
              />
            ))}

            <SectionRow title="IAA" span={dates.length + 2} />
            <MoneyRow
              label="IAA 小计"
              days={days}
              get={(d) => d.iaa.total}
              share={(d) => d.total}
            />
            {AGE_BUCKETS.map((b) => (
              <MoneyRow
                key={`iaa-${b.key}`}
                label={`IAA ${b.label}`}
                days={days}
                get={(d) => d.iaa[b.key]}
                share={(d) => d.iaa.total}
                muted
              />
            ))}

            <SectionRow title="IAP" span={dates.length + 2} />
            <MoneyRow
              label="IAP 小计"
              days={days}
              get={(d) => d.iap.total}
              share={(d) => d.total}
            />
            {AGE_BUCKETS.map((b) => (
              <MoneyRow
                key={`iap-${b.key}`}
                label={`IAP ${b.label}`}
                days={days}
                get={(d) => d.iap[b.key]}
                share={(d) => d.iap.total}
                muted
              />
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}

const DateCell = ({ value, onChange }) => (
  <Popover>
    <PopoverTrigger asChild>
      <Button variant="outline" size="sm" className="h-7 px-2 font-normal">
        <CalendarIcon className="mr-1 h-3.5 w-3.5" />
        {value}
      </Button>
    </PopoverTrigger>
    <PopoverContent className="w-auto p-0" align="end">
      <Calendar
        mode="single"
        selected={parseYmd(value)}
        onSelect={(date) => date && onChange(toYmd(date))}
        initialFocus
      />
    </PopoverContent>
  </Popover>
)

const SectionRow = ({ title, span }) => (
  <tr className="bg-gray-50">
    <td colSpan={span} className="px-2 py-1 text-xs font-medium text-gray-500">
      {title}
    </td>
  </tr>
)

const ValueRow = ({ label, days, render, bold, muted }) => (
  <tr className="hover:bg-gray-50">
    <td className={cn('p-2', muted ? 'text-gray-500 pl-4' : 'text-gray-700')}>
      {label}
    </td>
    {days.map((d, i) => (
      <td
        key={i}
        className={cn('p-2 text-right tabular-nums', bold && 'font-semibold')}
      >
        {d ? render(d) : '-'}
      </td>
    ))}
    <td />
  </tr>
)

// 金额行：主值为美元，下面一行小字显示占比（share 给出分母）
const MoneyRow = ({ label, days, get, share, bold, muted }) => (
  <ValueRow
    label={label}
    days={days}
    bold={bold}
    muted={muted}
    render={(d) => {
      const value = get(d)
      const base = share ? share(d) : 0
      return (
        <div className="flex flex-col items-end">
          <span className={cn(value < 0 && 'text-red-600')}>
            {formatUSD(value, 0, 0)}
          </span>
          {share && (
            <span className="text-xs text-gray-400">
              {base ? formatPercent(value / base, 1, 1) : '-'}
            </span>
          )}
        </div>
      )
    }}
  />
)

export default RevenueAgeStructure
